import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Users, Calendar, HandHeart, CalendarDays, FileText, ClipboardList, LogOut, Plus, Pencil, Trash2, LayoutTemplate, Loader2, Upload } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import AdminContent from './AdminContent';
import AdminContentHowWeHelp from './AdminContentHowWeHelp';
import AdminContentAbout from './AdminContentAbout';
import AdminContentContact from './AdminContentContact';
import AdminContentDonate from './AdminContentDonate';
import AdminContentCorporateLifeSeries from './AdminContentCorporateLifeSeries';
import AdminContentServices from './AdminContentServices';
import AdminContentKnowledgeHub from './AdminContentKnowledgeHub';
import AdminContentMembership from './AdminContentMembership';
import AdminContentEvents from './AdminContentEvents';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ label, value, sub, icon: Icon }) => (
  <Card className="rounded-2xl">
    <CardHeader className="flex flex-row items-center justify-between pb-2">
      <CardTitle className="text-sm font-medium text-gray-600">{label}</CardTitle>
      <Icon className="w-4 h-4 text-[#E74C3C]" />
    </CardHeader>
    <CardContent>
      <div className="text-3xl font-bold text-[#003366]">{value ?? 0}</div>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </CardContent>
  </Card>
);

const emptyEvent = { title: '', category: 'Community', event_date: '', event_time: '', format: 'Online', location: '', description: '', is_active: true };
const emptyArticle = { title: '', category: 'Career Transition', excerpt: '', body: '', link: '', cover_image: '', author: 'Equaltoo Team', published: true };
const emptyMember = { full_name: '', email: '', password: '' };

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [events, setEvents] = useState([]);
  const [articles, setArticles] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blockedDates, setBlockedDates] = useState([]);
  const [blockInput, setBlockInput] = useState('');

  const [eventDialog, setEventDialog] = useState({ open: false, data: emptyEvent, id: null });
  const [articleDialog, setArticleDialog] = useState({ open: false, data: emptyArticle, id: null });
  const [memberDialog, setMemberDialog] = useState({ open: false });
  const [memberForm, setMemberForm] = useState(emptyMember);
  const [coverUploading, setCoverUploading] = useState(false);
  const coverInputRef = useRef(null);

  const config = useCallback(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);

  const uploadCoverImage = async (file) => {
    if (!file) return;
    setCoverUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('key', 'article_cover');
      const r = await axios.post(`${API}/content/upload`, fd, config());
      setArticleDialog((prev) => ({ ...prev, data: { ...prev.data, cover_image: r.data.url } }));
      toast({ title: 'Cover image uploaded' });
    } catch {
      toast({ title: 'Upload failed', description: 'Please try again.', variant: 'destructive' });
    } finally {
      setCoverUploading(false);
    }
  };

  const fetchBlockedDates = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/bookings/blocked-dates`, config());
      setBlockedDates(r.data);
    } catch (e) {}
  }, [config]);

  const blockDay = async (dateStr) => {
    if (!dateStr) return;
    try {
      await axios.post(`${API}/bookings/block-date`, { date: dateStr, reason: '' }, config());
      fetchBlockedDates();
      toast({ description: `${dateStr} blocked.` });
    } catch (e) { toast({ description: e.response?.data?.detail || 'Error', variant: 'destructive' }); }
  };

  const blockWeek = async (dateStr) => {
    if (!dateStr) return;
    const start = new Date(dateStr + 'T00:00:00');
    const day = start.getDay();
    const monday = new Date(start);
    monday.setDate(start.getDate() - (day === 0 ? 6 : day - 1));
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      const ds = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      try { await axios.post(`${API}/bookings/block-date`, { date: ds, reason: 'Week block' }, config()); } catch (e) {}
    }
    fetchBlockedDates();
    toast({ description: 'Full week blocked.' });
  };

  const unblockDate = async (dateStr) => {
    try {
      await axios.delete(`${API}/bookings/block-date/${dateStr}`, config());
      fetchBlockedDates();
    } catch (e) {}
  };

  const fetchAll = useCallback(async () => {
    try {
      const c = config();
      const [s, m, b, v, e, a, r] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, c),
        axios.get(`${API}/members/`, c),
        axios.get(`${API}/bookings/all`, c),
        axios.get(`${API}/admin/volunteers`, c),
        axios.get(`${API}/events/all`, c),
        axios.get(`${API}/articles/all`, c),
        axios.get(`${API}/events/registrations`, c)
      ]);
      setStats(s.data); setMembers(m.data); setBookings(b.data);
      setVolunteers(v.data); setEvents(e.data); setArticles(a.data); setRegistrations(r.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [config]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.role !== 'admin') { navigate('/login'); return; }
    fetchAll();
    fetchBlockedDates();
  }, [navigate, fetchAll, fetchBlockedDates]);

  const handleLogout = () => {
    localStorage.removeItem('token'); localStorage.removeItem('user'); navigate('/login');
  };

  // Event CRUD
  const saveEvent = async () => {
    try {
      if (eventDialog.id) await axios.put(`${API}/events/${eventDialog.id}`, eventDialog.data, config());
      else await axios.post(`${API}/events/`, eventDialog.data, config());
      toast({ title: eventDialog.id ? 'Event updated' : 'Event created' });
      setEventDialog({ open: false, data: emptyEvent, id: null });
      fetchAll();
    } catch (err) { toast({ title: 'Error', description: err.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  const deleteEvent = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    await axios.delete(`${API}/events/${id}`, config()); toast({ title: 'Event deleted' }); fetchAll();
  };

  // Article CRUD
  const saveArticle = async () => {
    try {
      if (articleDialog.id) await axios.put(`${API}/articles/${articleDialog.id}`, articleDialog.data, config());
      else await axios.post(`${API}/articles/`, articleDialog.data, config());
      toast({ title: articleDialog.id ? 'Article updated' : 'Article created' });
      setArticleDialog({ open: false, data: emptyArticle, id: null });
      fetchAll();
    } catch (err) { toast({ title: 'Error', description: err.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  const deleteArticle = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    await axios.delete(`${API}/articles/${id}`, config()); toast({ title: 'Article deleted' }); fetchAll();
  };

  // Member add / delete
  const addMember = async () => {
    if (!memberForm.email || !memberForm.full_name) { toast({ title: 'Name and email required', variant: 'destructive' }); return; }
    try {
      await axios.post(`${API}/members/`, { ...memberForm, membership_plan: 'founding_member' });
      toast({ title: 'Member added', description: memberForm.password ? 'They can log in with the password you set.' : 'A temporary password was generated.' });
      setMemberDialog({ open: false }); setMemberForm(emptyMember); fetchAll();
    } catch (err) { toast({ title: 'Error', description: err.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  const deleteMember = async (id) => {
    if (!window.confirm('Delete this member and their login?')) return;
    await axios.delete(`${API}/members/${id}`, config()); toast({ title: 'Member deleted' }); fetchAll();
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FDFBF9]">Loading…</div>;
  const o = stats?.overview || {};

  return (
    <div className="min-h-screen bg-[#FDFBF9]">
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <h1 className="font-heading text-2xl font-bold text-[#003366]">Equaltoo Admin</h1>
          <Button onClick={handleLogout} variant="outline" size="sm" data-testid="admin-logout"><LogOut className="w-4 h-4 mr-2" />Logout</Button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <StatCard label="Members" value={o.total_members} sub={`${o.active_members || 0} active`} icon={Users} />
          <StatCard label="Volunteers" value={o.volunteers_count} sub="willing to give back" icon={HandHeart} />
          <StatCard label="Events" value={o.total_events} icon={CalendarDays} />
          <StatCard label="Registrations" value={o.total_registrations} icon={ClipboardList} />
          <StatCard label="Articles" value={o.total_articles} icon={FileText} />
          <StatCard label="Bookings" value={o.total_bookings} sub={`${o.recent_bookings || 0} this week`} icon={Calendar} />
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="mb-6 flex flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content">Site Content</TabsTrigger>
            <TabsTrigger value="members" data-testid="tab-members">Members</TabsTrigger>
            <TabsTrigger value="volunteers" data-testid="tab-volunteers">Volunteers</TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>
            <TabsTrigger value="articles" data-testid="tab-articles">Articles</TabsTrigger>
            <TabsTrigger value="registrations" data-testid="tab-registrations">Registrations</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="rounded-2xl">
                <CardHeader><CardTitle>Top Contribution Areas</CardTitle></CardHeader>
                <CardContent>
                  {(stats?.top_contributions || []).length === 0 ? <p className="text-sm text-gray-500">No contribution data yet.</p> : (
                    <div className="space-y-3">
                      {stats.top_contributions.map((t) => (
                        <div key={t.area} className="flex items-center justify-between">
                          <span className="text-sm text-gray-700">{t.area}</span>
                          <Badge className="bg-[#003366]">{t.count}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              <Card className="rounded-2xl">
                <CardHeader><CardTitle>Recent Members</CardTitle></CardHeader>
                <CardContent>
                  {(stats?.recent_members || []).map((m) => (
                    <div key={m.id} className="flex justify-between py-2 border-b last:border-0">
                      <div><div className="font-medium text-sm">{m.full_name}</div><div className="text-xs text-gray-500">{m.email}</div></div>
                      <span className="text-xs text-gray-400">{new Date(m.created_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* SITE CONTENT */}
          <TabsContent value="content">
            <Tabs defaultValue="home-content" className="w-full">
              <TabsList className="mb-4 flex flex-wrap h-auto gap-1">
                <TabsTrigger value="home-content" data-testid="subtab-home">Home</TabsTrigger>
                <TabsTrigger value="hwh-content" data-testid="subtab-hwh">How We Help</TabsTrigger>
                <TabsTrigger value="about-content" data-testid="subtab-about">About</TabsTrigger>
                <TabsTrigger value="contact-content" data-testid="subtab-contact">Contact</TabsTrigger>
                <TabsTrigger value="donate-content" data-testid="subtab-donate">Donate</TabsTrigger>
                <TabsTrigger value="cls-content" data-testid="subtab-cls">Corp Life Series</TabsTrigger>
                <TabsTrigger value="services-content" data-testid="subtab-services">Services</TabsTrigger>
                <TabsTrigger value="khub-content" data-testid="subtab-khub">Knowledge Hub</TabsTrigger>
                <TabsTrigger value="membership-content" data-testid="subtab-membership">Membership</TabsTrigger>
                <TabsTrigger value="events-content" data-testid="subtab-events">Events Page</TabsTrigger>
              </TabsList>
              <TabsContent value="home-content"><AdminContent /></TabsContent>
              <TabsContent value="hwh-content"><AdminContentHowWeHelp /></TabsContent>
              <TabsContent value="about-content"><AdminContentAbout /></TabsContent>
              <TabsContent value="contact-content"><AdminContentContact /></TabsContent>
              <TabsContent value="donate-content"><AdminContentDonate /></TabsContent>
              <TabsContent value="cls-content"><AdminContentCorporateLifeSeries /></TabsContent>
              <TabsContent value="services-content"><AdminContentServices /></TabsContent>
              <TabsContent value="khub-content"><AdminContentKnowledgeHub /></TabsContent>
              <TabsContent value="membership-content"><AdminContentMembership /></TabsContent>
              <TabsContent value="events-content"><AdminContentEvents /></TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="members">
            <div className="flex justify-end mb-4">
              <Button onClick={() => setMemberDialog({ open: true })} data-testid="new-member-btn" className="rounded-full bg-[#E74C3C] hover:bg-[#C0392B]"><Plus className="w-4 h-4 mr-1" />Add Member</Button>
            </div>
            <Card className="rounded-2xl"><CardContent className="pt-6 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left"><th className="py-3 px-3">Name</th><th className="py-3 px-3">Email</th><th className="py-3 px-3">Type</th><th className="py-3 px-3">Status</th><th className="py-3 px-3">Joined</th><th className="py-3 px-3">Actions</th></tr></thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id} className="border-b hover:bg-gray-50" data-testid="member-row">
                      <td className="py-3 px-3">{m.full_name}</td>
                      <td className="py-3 px-3">{m.email}</td>
                      <td className="py-3 px-3">Founding Member</td>
                      <td className="py-3 px-3"><span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">{m.membership_status}</span></td>
                      <td className="py-3 px-3">{new Date(m.start_date).toLocaleDateString()}</td>
                      <td className="py-3 px-3"><Button size="icon" variant="ghost" onClick={() => deleteMember(m.id)} data-testid="delete-member-btn"><Trash2 className="w-4 h-4 text-red-500" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent></Card>
          </TabsContent>

          {/* VOLUNTEERS */}
          <TabsContent value="volunteers">
            <Card className="rounded-2xl"><CardContent className="pt-6 overflow-x-auto">
              {volunteers.length === 0 ? <p className="text-sm text-gray-500">No volunteers yet.</p> : (
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left"><th className="py-3 px-3">Name</th><th className="py-3 px-3">Role</th><th className="py-3 px-3">Contribution Areas</th><th className="py-3 px-3">Sessions/yr</th><th className="py-3 px-3">Format</th><th className="py-3 px-3">Audience</th></tr></thead>
                  <tbody>
                    {volunteers.map((v) => (
                      <tr key={v.id} className="border-b hover:bg-gray-50 align-top" data-testid="volunteer-row">
                        <td className="py-3 px-3"><div className="font-medium">{v.full_name}</div><div className="text-xs text-gray-500">{v.email}</div></td>
                        <td className="py-3 px-3">{v.designation}{v.company ? `, ${v.company}` : ''}<div className="text-xs text-gray-500">{v.city}</div></td>
                        <td className="py-3 px-3"><div className="flex flex-wrap gap-1 max-w-xs">{(v.contribution_areas || []).map((a) => <Badge key={a} variant="secondary" className="text-xs">{a}</Badge>)}</div></td>
                        <td className="py-3 px-3">{v.sessions_per_year}</td>
                        <td className="py-3 px-3">{v.preferred_format}</td>
                        <td className="py-3 px-3"><div className="flex flex-wrap gap-1 max-w-[160px]">{(v.preferred_audience || []).map((a) => <span key={a} className="text-xs text-gray-600">{a}</span>)}</div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent></Card>
          </TabsContent>

          {/* EVENTS */}
          <TabsContent value="events">
            <div className="flex justify-end mb-4">
              <Button onClick={() => setEventDialog({ open: true, data: emptyEvent, id: null })} data-testid="new-event-btn" className="rounded-full bg-[#E74C3C] hover:bg-[#C0392B]"><Plus className="w-4 h-4 mr-1" />New Event</Button>
            </div>
            <Card className="rounded-2xl"><CardContent className="pt-6 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left"><th className="py-3 px-3">Title</th><th className="py-3 px-3">Category</th><th className="py-3 px-3">Date</th><th className="py-3 px-3">Format</th><th className="py-3 px-3">Active</th><th className="py-3 px-3">Actions</th></tr></thead>
                <tbody>
                  {events.map((e) => (
                    <tr key={e.id} className="border-b hover:bg-gray-50" data-testid="event-row">
                      <td className="py-3 px-3 font-medium">{e.title}</td>
                      <td className="py-3 px-3">{e.category}</td>
                      <td className="py-3 px-3">{e.event_date}</td>
                      <td className="py-3 px-3">{e.format}</td>
                      <td className="py-3 px-3">{e.is_active ? 'Yes' : 'No'}</td>
                      <td className="py-3 px-3 flex gap-2">
                        <Button size="icon" variant="ghost" onClick={() => setEventDialog({ open: true, data: { ...emptyEvent, ...e }, id: e.id })} data-testid="edit-event-btn"><Pencil className="w-4 h-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => deleteEvent(e.id)} data-testid="delete-event-btn"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent></Card>
          </TabsContent>

          {/* ARTICLES */}
          <TabsContent value="articles">
            <div className="flex justify-end mb-4">
              <Button onClick={() => setArticleDialog({ open: true, data: emptyArticle, id: null })} data-testid="new-article-btn" className="rounded-full bg-[#E74C3C] hover:bg-[#C0392B]"><Plus className="w-4 h-4 mr-1" />New Article</Button>
            </div>
            <Card className="rounded-2xl"><CardContent className="pt-6 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left"><th className="py-3 px-3">Title</th><th className="py-3 px-3">Category</th><th className="py-3 px-3">Published</th><th className="py-3 px-3">Actions</th></tr></thead>
                <tbody>
                  {articles.map((a) => (
                    <tr key={a.id} className="border-b hover:bg-gray-50" data-testid="article-row">
                      <td className="py-3 px-3 font-medium max-w-md">{a.title}</td>
                      <td className="py-3 px-3">{a.category}</td>
                      <td className="py-3 px-3">{a.published ? 'Yes' : 'Draft'}</td>
                      <td className="py-3 px-3 flex gap-2">
                        <Button size="icon" variant="ghost" onClick={() => setArticleDialog({ open: true, data: { ...emptyArticle, ...a }, id: a.id })} data-testid="edit-article-btn"><Pencil className="w-4 h-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => deleteArticle(a.id)} data-testid="delete-article-btn"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent></Card>
          </TabsContent>

          {/* REGISTRATIONS */}
          <TabsContent value="registrations">
            <Card className="rounded-2xl"><CardContent className="pt-6 overflow-x-auto">
              {registrations.length === 0 ? <p className="text-sm text-gray-500">No registrations yet.</p> : (
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left"><th className="py-3 px-3">Name</th><th className="py-3 px-3">Email</th><th className="py-3 px-3">Session / Event</th><th className="py-3 px-3">Type</th><th className="py-3 px-3">When</th></tr></thead>
                  <tbody>
                    {registrations.map((r) => (
                      <tr key={r.id} className="border-b hover:bg-gray-50" data-testid="registration-row">
                        <td className="py-3 px-3">{r.name}</td>
                        <td className="py-3 px-3">{r.email}</td>
                        <td className="py-3 px-3">{r.title}</td>
                        <td className="py-3 px-3 capitalize">{r.kind}</td>
                        <td className="py-3 px-3">{new Date(r.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent></Card>
          </TabsContent>

          {/* BOOKINGS */}
          <TabsContent value="bookings">
            {/* Manage availability */}
            <Card className="rounded-2xl mb-4">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-[#003366]">Block Dates / Availability</CardTitle>
                <p className="text-xs text-gray-500">Blocked dates are greyed out in the member booking calendar so no one can book them.</p>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 items-center mb-4">
                  <input
                    type="date"
                    value={blockInput}
                    onChange={(e) => setBlockInput(e.target.value)}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#003366]"
                    data-testid="block-date-input"
                  />
                  <Button size="sm" onClick={() => blockDay(blockInput)} disabled={!blockInput} className="rounded-full bg-[#003366] hover:bg-[#002244] text-white" data-testid="block-day-btn">
                    Block this day
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => blockWeek(blockInput)} disabled={!blockInput} className="rounded-full border-[#003366] text-[#003366]" data-testid="block-week-btn">
                    Block this week (Mon–Sun)
                  </Button>
                </div>
                {blockedDates.length === 0 ? (
                  <p className="text-sm text-gray-400">No dates blocked — all days are open for bookings.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {blockedDates.map((d) => (
                      <span key={d.date} className="inline-flex items-center gap-1.5 bg-red-50 border border-red-200 text-red-700 rounded-full px-3 py-1 text-xs font-medium" data-testid="blocked-date-chip">
                        {d.date}
                        <button onClick={() => unblockDate(d.date)} className="hover:text-red-900 font-bold leading-none" title="Unblock">×</button>
                      </span>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            {/* Bookings table */}
            <Card className="rounded-2xl"><CardContent className="pt-6 overflow-x-auto">
              {bookings.length === 0 ? <p className="text-sm text-gray-500">No bookings yet.</p> : (
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left"><th className="py-3 px-3">Member</th><th className="py-3 px-3">Service</th><th className="py-3 px-3">Date</th><th className="py-3 px-3">Time</th><th className="py-3 px-3">Status</th></tr></thead>
                  <tbody>
                    {bookings.map((b) => (
                      <tr key={b.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-3">{b.user_name}</td>
                        <td className="py-3 px-3 capitalize">{b.service_type}</td>
                        <td className="py-3 px-3">{new Date(b.appointment_date).toLocaleDateString()}</td>
                        <td className="py-3 px-3">{b.appointment_time}</td>
                        <td className="py-3 px-3">{b.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent></Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Event Dialog */}
      <Dialog open={eventDialog.open} onOpenChange={(v) => setEventDialog({ ...eventDialog, open: v })}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{eventDialog.id ? 'Edit Event' : 'New Event'}</DialogTitle><DialogDescription>Fill in the event details below.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={eventDialog.data.title} onChange={(e) => setEventDialog({ ...eventDialog, data: { ...eventDialog.data, title: e.target.value } })} data-testid="event-title-input" className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Category</Label><Input value={eventDialog.data.category} onChange={(e) => setEventDialog({ ...eventDialog, data: { ...eventDialog.data, category: e.target.value } })} className="mt-1" /></div>
              <div><Label>Date (e.g. 12 Jul 2026)</Label><Input value={eventDialog.data.event_date} onChange={(e) => setEventDialog({ ...eventDialog, data: { ...eventDialog.data, event_date: e.target.value } })} data-testid="event-date-input" className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Time</Label><Input value={eventDialog.data.event_time} onChange={(e) => setEventDialog({ ...eventDialog, data: { ...eventDialog.data, event_time: e.target.value } })} className="mt-1" /></div>
              <div><Label>Format</Label>
                <Select value={eventDialog.data.format} onValueChange={(v) => setEventDialog({ ...eventDialog, data: { ...eventDialog.data, format: v } })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Online">Online</SelectItem><SelectItem value="Offline">Offline</SelectItem><SelectItem value="Hybrid">Hybrid</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Location</Label><Input value={eventDialog.data.location} onChange={(e) => setEventDialog({ ...eventDialog, data: { ...eventDialog.data, location: e.target.value } })} className="mt-1" /></div>
            <div><Label>Description</Label><Textarea value={eventDialog.data.description} onChange={(e) => setEventDialog({ ...eventDialog, data: { ...eventDialog.data, description: e.target.value } })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button onClick={saveEvent} data-testid="save-event-btn" className="rounded-full bg-[#E74C3C] hover:bg-[#C0392B]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Article Dialog */}
      <Dialog open={articleDialog.open} onOpenChange={(v) => setArticleDialog({ ...articleDialog, open: v })}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{articleDialog.id ? 'Edit Article' : 'New Article'}</DialogTitle><DialogDescription>Write or edit the article content below.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={articleDialog.data.title} onChange={(e) => setArticleDialog({ ...articleDialog, data: { ...articleDialog.data, title: e.target.value } })} data-testid="article-title-input" className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Category</Label><Input value={articleDialog.data.category} onChange={(e) => setArticleDialog({ ...articleDialog, data: { ...articleDialog.data, category: e.target.value } })} data-testid="article-category-input" className="mt-1" /></div>
              <div><Label>Author</Label><Input value={articleDialog.data.author} onChange={(e) => setArticleDialog({ ...articleDialog, data: { ...articleDialog.data, author: e.target.value } })} className="mt-1" /></div>
            </div>
            <div><Label>Excerpt</Label><Textarea value={articleDialog.data.excerpt} onChange={(e) => setArticleDialog({ ...articleDialog, data: { ...articleDialog.data, excerpt: e.target.value } })} className="mt-1" /></div>
            <div><Label>Body</Label><Textarea value={articleDialog.data.body} onChange={(e) => setArticleDialog({ ...articleDialog, data: { ...articleDialog.data, body: e.target.value } })} className="mt-1 min-h-[140px]" data-testid="article-body-input" /></div>
            <div>
              <Label>Cover Image</Label>
              <div className="mt-1 flex items-center gap-3">
                {articleDialog.data.cover_image && (
                  <img src={articleDialog.data.cover_image} alt="cover" className="w-16 h-16 rounded-lg object-cover border border-[#EAE5E0] flex-shrink-0" />
                )}
                <div className="flex-1 flex flex-col gap-1.5">
                  <button
                    type="button"
                    onClick={() => coverInputRef.current?.click()}
                    disabled={coverUploading}
                    data-testid="upload-cover-btn"
                    className="inline-flex items-center gap-2 rounded-full border border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
                  >
                    {coverUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {coverUploading ? 'Uploading…' : articleDialog.data.cover_image ? 'Replace photo' : 'Upload photo'}
                  </button>
                  <input
                    ref={coverInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => uploadCoverImage(e.target.files?.[0])}
                    data-testid="cover-image-file-input"
                  />
                  {articleDialog.data.cover_image && (
                    <p className="text-xs text-[#888] truncate max-w-[220px]">{articleDialog.data.cover_image}</p>
                  )}
                </div>
              </div>
            </div>
            <div><Label>External Link (optional)</Label><Input value={articleDialog.data.link} onChange={(e) => setArticleDialog({ ...articleDialog, data: { ...articleDialog.data, link: e.target.value } })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button onClick={saveArticle} data-testid="save-article-btn" className="rounded-full bg-[#E74C3C] hover:bg-[#C0392B]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Member Dialog */}
      <Dialog open={memberDialog.open} onOpenChange={(v) => setMemberDialog({ open: v })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add Member</DialogTitle><DialogDescription>Create a member. Set a password to let them log in to the member portal.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Full Name</Label><Input value={memberForm.full_name} onChange={(e) => setMemberForm({ ...memberForm, full_name: e.target.value })} data-testid="member-name-input" className="mt-1" /></div>
            <div><Label>Email</Label><Input type="email" value={memberForm.email} onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })} data-testid="member-email-input" className="mt-1" /></div>
            <div><Label>Password (optional — for portal login)</Label><Input value={memberForm.password} onChange={(e) => setMemberForm({ ...memberForm, password: e.target.value })} data-testid="member-password-input" className="mt-1" placeholder="Leave blank for auto password" /></div>
          </div>
          <DialogFooter><Button onClick={addMember} data-testid="save-member-btn" className="rounded-full bg-[#E74C3C] hover:bg-[#C0392B]">Add Member</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
